import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { userService, installationService } from '../services/api';

export default function Workers() {
  const [workers, setWorkers] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadWorkers();
  }, []);

  const loadWorkers = async () => {
    try {
      const [workersResponse, installationsResponse] = await Promise.all([
        userService.getUsers({ role: 'worker' }),
        installationService.getInstallations(),
      ]);

      setWorkers(workersResponse.data);
      setInstallations(installationsResponse.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get installation count for a worker
   */
  const getWorkerInstallationCount = (workerId) => {
    return installations.filter((inst) => inst.installer?._id === workerId).length;
  };

  /**
   * Filter workers based on search term
   */
  const filteredWorkers = workers.filter((worker) =>
    worker.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.team?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.phone?.includes(searchTerm)
  );

  const handleAssignCredit = (worker) => {
    setSelectedWorker(worker);
    setCreditAmount('');
    setDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const handleConfirmCredit = async () => {
    const amount = parseInt(creditAmount);
    if (!amount || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      await userService.addCredit(selectedWorker._id, amount);
      setSuccess(`Successfully added ${amount} meter(s) to ${selectedWorker.username}`);
      setDialogOpen(false);
      loadWorkers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    {
      field: 'username',
      headerName: 'Username',
      width: 130,
      renderCell: (params) => (
        <strong>{params.value}</strong>
      ),
    },
    {
      field: 'full_name',
      headerName: 'Full Name',
      width: 170,
    },
    {
      field: 'team',
      headerName: 'Team',
      width: 130,
      valueGetter: (params) => params.row.team?.name || 'Not assigned',
    },
    {
      field: 'meter_credit',
      headerName: 'Meter Credit',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={`${params.value}`}
          color={params.value > 0 ? 'success' : 'error'}
          size='small'
        />
      ),
    },
    {
      field: 'installations',
      headerName: 'Installations',
      width: 120,
      renderCell: (params) => {
        const count = getWorkerInstallationCount(params.row._id);
        return (
          <Chip
            label={`${count} done`}
            color='primary'
            variant='outlined'
            size='small'
          />
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 110,
      valueGetter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      renderCell: (params) => (
        <Button
          variant='contained'
          size='small'
          startIcon={<AddIcon />}
          onClick={() => handleAssignCredit(params.row)}
        >
          Add Credit
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h4'>Field Workers Management</Typography>
      </Box>

      {error && !dialogOpen && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Search Field */}
          <TextField
            fullWidth
            variant='outlined'
            placeholder='Search by username, full name, team, or phone...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Workers Table */}
          <DataGrid
            rows={filteredWorkers}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Assign Credit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Assign Meter Credit</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant='body2' color='text.secondary' paragraph>
            Worker: <strong>{selectedWorker?.full_name || selectedWorker?.username}</strong>
          </Typography>
          <Typography variant='body2' color='text.secondary' paragraph>
            Current Credit: <strong>{selectedWorker?.meter_credit} meters</strong>
          </Typography>
          <TextField
            autoFocus
            margin='dense'
            label='Credit Amount to Add'
            type='number'
            fullWidth
            variant='outlined'
            value={creditAmount}
            onChange={(e) => setCreditAmount(e.target.value)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmCredit} variant='contained'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

